import { createApiClient } from '@/utils/api/ApiDefault';
import { AzureRefreshToken } from '@/types/api';
import { deleteCookie, getCookie, setCookie } from '@/utils/tokens';
import {
  COOKIE_KEY_MS_ACCESS_TOKEN,
  COOKIE_KEY_MS_REFRESH_TOKEN,
} from '@/constant/keys';
import axios from 'axios';
import {
  requestRNToken,
  RnMessage,
  sendSentry,
  setAuthorizationHeader,
} from '@/utils/function';
import { twoWeek } from '@/constant';
import { createMsAuthExpiredError } from '@/utils/api/msAzure/authError';

const axiosInstance = createApiClient();

axiosInstance.interceptors.request.use(async (config) => {
  // NOTE: 요청 전달되기 전 작업 처리
  const extraAuthorization = config.headers.Authorization;

  if (extraAuthorization) {
    if (process.env.NODE_ENV === 'development')
      console.log(`👉 Extra Auth 존재`);
  } else {
    config.headers.Authorization = `Bearer ${getCookie(
      COOKIE_KEY_MS_ACCESS_TOKEN,
    )}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  // NOTE: 응답 200번대 status일 때 응답 성공 직전 호출
  // NOTE: 이 작업 이후 .then()으로 이어진다
  (response) => response,
  // NOTE: 응답 200번대가 아닌 status일 때 응답 에러 직전 호출
  // NOTE: 이 작업 이후 .catch()로 이어진다
  async (err) => {
    const originalRequest = err.config || {};

    const status = err?.response?.status;
    const code = err?.response?.data?.code;

    if (status === 401 || (code === '2002' && !originalRequest._retry)) {
      // 앱 경로는 손대지 않는다. 네이티브의 GET_MS_NEW_TOKEN 계약이 확인되지 않아
      // MsAuthExpired 로 넘겨도 위젯 버튼이 실제로 통할지 보장할 수 없다.
      if ((window as any).isRNWebview2) {
        await requestRNToken(RnMessage.GET_MS_NEW_TOKEN);
        const newAccessToken = getCookie(COOKIE_KEY_MS_ACCESS_TOKEN);
        setAuthorizationHeader(originalRequest, newAccessToken as string);
        return axiosInstance(originalRequest);
      } else {
        const refreshToken = getCookie(COOKIE_KEY_MS_REFRESH_TOKEN);

        // 여기서 /login 으로 튕기지 않는다. 포탈 JWT 는 살아있으므로 MS 위젯만 재인증하면 된다.
        if (!refreshToken || refreshToken === 'undefined') {
          return Promise.reject(createMsAuthExpiredError(err));
        }

        originalRequest._retry = true;

        try {
          const token = await axios
            .get<AzureRefreshToken>(
              `${process.env.NEXT_PUBLIC_API_URL}/api/auth/azure/token/refresh`,
              {
                headers: {
                  authorization: `Bearer ${refreshToken}`,
                },
              },
            )
            .then((res) => res.data.data);

          const { access_token, expires_at, refresh_token } = token;

          setCookie(COOKIE_KEY_MS_ACCESS_TOKEN, access_token, expires_at);
          setCookie(COOKIE_KEY_MS_REFRESH_TOKEN, refresh_token, twoWeek);

          originalRequest.headers.authorization = `Bearer ${access_token}`;

          return axios(originalRequest);
        } catch (refreshError) {
          // refresh 토큰이 만료·폐기된 경우. 그대로 두면 매 요청이 같은 실패를 반복하므로 지운다.
          sendSentry(
            axios.isAxiosError(refreshError) ? refreshError : err,
            'MS_REFRESH_FAILED',
          );
          deleteCookie(COOKIE_KEY_MS_ACCESS_TOKEN);
          deleteCookie(COOKIE_KEY_MS_REFRESH_TOKEN);

          return Promise.reject(createMsAuthExpiredError(refreshError));
        }
      }
    }
    if ((window as any).isRNWebview2) {
      const sendData = JSON.stringify({
        actionType: 'FORCE_LOGOUT',
      });
      window?.ReactNativeWebView?.postMessage(sendData);
      // Promise chaining 끊기
      return Promise.reject(err);
    }

    if (status < 400 || status >= 500) {
      sendSentry(err, 'MS');
    }
    return Promise.reject(err);
  },
);

export { axiosInstance as msInstance };
